import { getCSRFToken } from '../utils/getCSRFToken';
import { formatDateISO } from '../utils/dateUtils';
import { processVerticalImageSmart } from '../utils/borderDetection';
import Quill from 'quill';
import Pica from 'pica';

export default function registerCalendarSettingsStore() {
  const store = Alpine.store('calendarSettingsStore', {
    showCreateExtraModal: false,
    showEditExtraModal: false,
    newExtra: {
      name: '',
      description: '',
      unit_price: 0,
      image: null,
      imagePreview: null,
      published: true,
      associated_rate_sets: [],
      dropdown_field_title: '',
      dropdown_options_text: '',
      unit_label_singular: '',
      unit_label_plural: '',
      pricing_basis: 'per_item',
    },
    extraType: 'general', // Track which type is selected
    editingExtra: {
      id: null,
      name: '',
      description: '',
      unit_price: 0,
      image: null,
      imagePreview: null,
      published: true,
      associated_rate_sets: [],
      dropdown_field_title: '',
      dropdown_options_text: '',
      unit_label_singular: '',
      unit_label_plural: '',
      pricing_basis: 'per_item',
    },
    isLoading: false,
    error: null,
    calendarId: null,
    imageProcessing: false,
    createExtraImageProcessing: false,
    tax_fees: [],
    userPermission: [],
    availableTaxes: [],
    availableFees: [],
    extras: [],
    quillHelpText: null,
    stripeAccountStatus: null,
    paypalAccountComplete: false,
    // Add toolbarOptions at the store level
    toolbarOptions: [
      [{ header: [4, 5, false] }],
      ['bold', 'italic', 'underline', 'link'],
      [{ list: 'ordered' }, { list: 'bullet' }],
    ],
    invoicexpressEnabled: false,

    // Store Customization Properties
    selectedTheme: 'classic',
    themes: {},
    customStoreColor: '#ffffff',
    customButtonColor: '#212529',
    storeHeaderColor: '#ffffff',
    headerShouldMatchStore: false, // Track user's intent to match header to store color
    sharpMode: false,
    roundedMode: false,
    imageDisplayMode: 'LS', // Default to landscape

    // Custom Fields Properties
    customFields: [],
    showAddFieldModal: false,
    showEditFieldModal: false,
    isSavingField: false,
    currentField: {
      field_name: '',
      field_type: 'text',
      field_label: '',
      field_placeholder: '',
      is_active: true,
      order: 0,
      options: [],
      validation_rules: {},
    },
    dropdownOptionsText: '',

    // Listing Custom Fields Properties
    listingFields: [],
    showAddListingFieldModal: false,
    showEditListingFieldModal: false,
    isSavingListingField: false,
    currentListingField: {
      field_name: '',
      field_type: 'text',
      field_label: '',
      field_placeholder: '',
      is_required: true,
      is_active: true,
      applies_to: 'booking_item',
      order: 0,
      options: [],
      help_text: '',
      validation_rules: {},
      selected_ratesets: [], // New: track rateset selections
    },
    listingDropdownOptionsText: '',
    ratesetFieldCounts: {}, // Track how many active fields each rateset has
    maxListingCustomFields: 2,

    detectTransparency(canvas) {
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Check every 4th pixel (RGBA) for alpha channel
      // Skip some pixels for performance on large images
      const step = Math.max(
        1,
        Math.floor((canvas.width * canvas.height) / 10000),
      ); // Sample up to 10,000 pixels

      for (let i = 3; i < data.length; i += 4 * step) {
        if (data[i] < 255) {
          // Alpha channel less than 255 means transparency
          return true;
        }
      }

      return false;
    },

    initializeSettingsContext() {
      const container = document.getElementById('calendarSettingsContainer');
      if (container) {
        this.calendarId = container.dataset.calendarId;
        this.userPermission = container.dataset.userPermission;
        this.bookingMode = container.dataset.bookingMode;
        this.previousBookingMode = container.dataset.bookingMode; // Store initial value
        this.invoicexpressEnabled =
          container.dataset.invoicexpressEnabled === 'true';
        this.paypalAccountComplete =
          container.dataset.paypalAccountComplete === 'true';
        const maxFields = parseInt(
          container.dataset.maxListingCustomFields,
          10,
        );
        this.maxListingCustomFields = Number.isNaN(maxFields) ? 2 : maxFields;
        console.log('User permission:', this.userPermission);
      }
      console.log(
        'CalendarSettingsStore initialized with calendar ID:',
        this.calendarId,
      );
    },

    initializeCalendarSettings() {
      this.initializeSettingsContext();
      this.fetchStripeAccountStatus();

      // Initialize terms Quill if available on this store
      setTimeout(() => {
        const editor = document.querySelector('[x-ref="termsQuillEditor"]');
        if (editor && typeof this.initQuill === 'function') {
          this.initQuill(editor);
        }
      }, 100);

      // Load all necessary data
      if (typeof this.loadDepositOptions === 'function') {
        this.loadDepositOptions();
      }
      this.loadExtras();
      this.loadTaxFees();
      if (typeof this.loadDiscounts === 'function') {
        this.loadDiscounts();
      }
      this.fetchCustomFields(); // Load custom fields
      this.initLogoPreview();
      if (typeof this.initBookingModeWatcher === 'function') {
        this.initBookingModeWatcher();
      }
      if (typeof this.initializeSlug === 'function') {
        this.initializeSlug();
      }
      if (typeof this.initializeAvailableYears === 'function') {
        this.initializeAvailableYears();
      }
      window.setTimeout(() => this.ensureModalBindings(), 0);
    },
    ensureModalBindings() {
      const alpine = window.Alpine;
      if (!alpine || typeof alpine.initTree !== 'function') {
        return;
      }

      const modalRoots = document.querySelectorAll('[data-modal-root="true"]');
      modalRoots.forEach((el) => {
        // If Alpine has not initialized this node, initialize it explicitly.
        if (!el._x_marker && !el._x_dataStack) {
          alpine.initTree(el);
        }
      });
    },

    async fetchStripeAccountStatus() {
      try {
        const response = await fetch(
          `/calendar/fetch-stripe-account-status/?calendar_id=${this.calendarId}`,
        );
        const data = await response.json();

        if (response.ok) {
          this.stripeAccountStatus = data;
          // Only log if there's an actual connection or error
          if (data.connected) {
            console.log('Stripe account connected:', data);
          }
        } else {
          console.error('Error fetching Stripe account status:', data.error);
          this.stripeAccountStatus = {
            connected: false,
            charges_enabled: false,
            payouts_enabled: false,
            requirements: [],
          };
        }
      } catch (error) {
        console.error('Error fetching Stripe account status:', error);
        this.stripeAccountStatus = {
          connected: false,
          charges_enabled: false,
          payouts_enabled: false,
          requirements: [],
        };
      }
    },

    // Initialize logo preview functionality
    initLogoPreview() {
      const logoInput = document.getElementById('id_logo');
      if (logoInput) {
        logoInput.addEventListener('change', this.handleLogoChange.bind(this));
      }

      const faviconInput = document.getElementById('id_favicon');
      if (faviconInput) {
        faviconInput.addEventListener(
          'change',
          this.handleFaviconChange.bind(this),
        );
      }
    },

    // Handle logo file selection
    async handleLogoChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Define reasonable size limits
      const maxSizeForWarning = 5 * 1024 * 1024; // 5MB warning threshold
      const targetSize = 500 * 1024; // Balanced size/quality for crisp email logos

      // If file is already small enough, just use it directly
      if (file.size <= targetSize) {
        console.log('Logo file is already an acceptable size, using as-is');

        // Hide the "No logo" text if it exists
        const noLogoText = document.getElementById('noLogoText');
        if (noLogoText) noLogoText.style.display = 'none';

        // Hide the current logo if it exists
        const currentLogo = document.getElementById('currentLogo');
        if (currentLogo) currentLogo.style.display = 'none';

        // Show the preview image using FileReader
        const preview = document.getElementById('logoPreview');
        if (preview) {
          preview.style.display = 'inline-block';
          const reader = new FileReader();
          reader.onload = (e) => {
            preview.src = e.target.result;
          };
          reader.readAsDataURL(file);
        }
        return;
      }

      // For larger files, proceed with resizing
      if (file.size > maxSizeForWarning) {
        console.warn('Large logo file detected, processing may take longer');
      }

      // Hide the "No logo" text if it exists
      const noLogoText = document.getElementById('noLogoText');
      if (noLogoText) noLogoText.style.display = 'none';

      // Hide the current logo if it exists
      const currentLogo = document.getElementById('currentLogo');
      if (currentLogo) currentLogo.style.display = 'none';

      // Show the preview image using FileReader (immediately for better UX)
      const preview = document.getElementById('logoPreview');
      if (preview) {
        preview.style.display = 'inline-block';

        // Show a loading indicator if needed
        this.imageProcessing = true;

        try {
          // Check if Pica is available
          if (typeof Pica !== 'function') {
            console.warn('Pica library not found, using basic image handling');
            // Basic handling without resizing
            const reader = new FileReader();
            reader.onload = (e) => {
              preview.src = e.target.result;
              this.imageProcessing = false;
            };
            reader.readAsDataURL(file);
            return;
          }

          // Use Pica for high-quality resizing
          const pica = new Pica({
            features: ['js', 'wasm'], // Use JS and WASM but not web workers due to CSP restrictions
          });
          const reader = new FileReader();

          reader.onload = async (e) => {
            const img = new Image();
            img.src = e.target.result;

            img.onload = async () => {
              // For logos, keep enough resolution for email clients without oversized files.
              const maxLogoSize = 800;

              // Only resize if the image is larger than our max dimensions
              let needsResize =
                img.width > maxLogoSize || img.height > maxLogoSize;

              // Calculate dimensions while preserving aspect ratio
              let width = img.width;
              let height = img.height;

              if (needsResize) {
                if (width > height) {
                  if (width > maxLogoSize) {
                    height = Math.round(height * (maxLogoSize / width));
                    width = maxLogoSize;
                  }
                } else {
                  if (height > maxLogoSize) {
                    width = Math.round(width * (maxLogoSize / height));
                    height = maxLogoSize;
                  }
                }
              }

              // If no resize needed and file size is acceptable, use original
              if (!needsResize) {
                preview.src = e.target.result;
                this.imageProcessing = false;
                return;
              }

              // Create canvas for resizing with proper context
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d', {
                alpha: true,
                willReadFrequently: true,
              });

              canvas.width = width;
              canvas.height = height;

              // Clear canvas with transparency
              ctx.clearRect(0, 0, width, height);

              // Draw the image to the original canvas with high quality settings
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              ctx.drawImage(img, 0, 0, width, height);

              // Use Pica to resize with high quality
              const resizedCanvas = document.createElement('canvas');
              resizedCanvas.width = width;
              resizedCanvas.height = height;

              // Use Pica with high quality options
              await pica.resize(canvas, resizedCanvas, {
                unsharpAmount: 80,
                unsharpRadius: 0.6,
                unsharpThreshold: 2,
                alpha: true,
              });

              // Determine output format based on input
              const isPNG =
                file.type === 'image/png' ||
                file.name.toLowerCase().endsWith('.png');
              const outputType = isPNG ? 'image/png' : 'image/jpeg';

              // Set a higher quality floor - don't go below 0.9 for logos
              let quality = 0.95; // Start with very high quality
              let dataUrl = resizedCanvas.toDataURL(outputType, quality);

              // Convert data URL to Blob to check size
              let blob = await this.dataUrlToBlob(dataUrl);

              // Only reduce quality if the file is still too large
              if (!isPNG && blob.size > targetSize) {
                while (blob.size > targetSize && quality > 0.9) {
                  quality -= 0.02; // More gradual quality reduction
                  dataUrl = resizedCanvas.toDataURL(outputType, quality);
                  blob = await this.dataUrlToBlob(dataUrl);
                }
              }

              // Update the preview
              preview.src = dataUrl;

              // Create a new File from the blob for the form submission
              const processedFile = new File([blob], file.name, {
                type: outputType,
              });

              // Replace the original file in the input
              const dataTransfer = new DataTransfer();
              dataTransfer.items.add(processedFile);
              event.target.files = dataTransfer.files;

              this.imageProcessing = false;
            };
          };

          reader.readAsDataURL(file);
        } catch (error) {
          console.error('Error processing logo:', error);
          // Fallback to basic handling
          const reader = new FileReader();
          reader.onload = (e) => {
            preview.src = e.target.result;
            this.imageProcessing = false;
          };
          reader.readAsDataURL(file);
        }
      }
    },

    handleFaviconChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      const noFaviconText = document.getElementById('noFaviconText');
      if (noFaviconText) noFaviconText.style.display = 'none';

      const currentFavicon = document.getElementById('currentFavicon');
      if (currentFavicon) currentFavicon.style.display = 'none';

      const preview = document.getElementById('faviconPreview');
      if (preview) {
        preview.style.display = 'inline-block';
        const reader = new FileReader();
        reader.onload = (e) => {
          preview.src = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    // Helper method to convert data URL to Blob
    dataUrlToBlob(dataUrl) {
      return new Promise((resolve) => {
        const binaryString = atob(dataUrl.split(',')[1]);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        resolve(
          new Blob([bytes], {
            type: dataUrl.split(',')[0].split(':')[1].split(';')[0],
          }),
        );
      });
    },

    loadExtras() {
      try {
        const extrasScript = document.getElementById('extras-data');
        if (extrasScript) {
          const extrasData = extrasScript.textContent;
          this.extras = JSON.parse(extrasData);
          console.log('Extras loaded:', this.extras);
        } else {
          this.extras = [];
        }
      } catch (error) {
        console.error('Error loading extras:', error);
        this.extras = [];
      }
    },

    loadTaxFees() {
      try {
        const taxFeesScript = document.getElementById('taxfees-data');
        if (taxFeesScript) {
          const taxFeesData = taxFeesScript.textContent;
          this.tax_fees = JSON.parse(taxFeesData);

          // Separate into taxes and fees
          this.availableTaxes = this.tax_fees.filter((tf) => tf.is_tax);
          this.availableFees = this.tax_fees.filter((tf) => !tf.is_tax);

          console.log('Tax fees loaded:', this.tax_fees);
          console.log('Available Taxes:', this.availableTaxes);
          console.log('Available Fees:', this.availableFees);
        } else {
          console.log('No taxfees script found');
          this.availableTaxes = [];
          this.availableFees = [];
        }
      } catch (error) {
        console.error('Error loading tax fees:', error);
        this.availableTaxes = [];
        this.availableFees = [];
      }
    },

    // Reset new extra form
    resetNewExtra() {
      this.newExtra = {
        name: '',
        description: '',
        unit_price: 0,
        image: null,
        imagePreview: null,
        published: true,
        is_addon: false,
        tax_fees: [],
        associated_rate_sets: [],
        dropdown_field_title: '',
        dropdown_options_text: '',
        unit_label_singular: '',
        unit_label_plural: '',
        pricing_basis: 'per_item',
        duration_adjustment: 0,
      };
      this.extraType = 'general';
      this.createExtraImageProcessing = false;
    },

    // Remove extra image
    removeExtraImage() {
      this.newExtra.image = null;
      this.newExtra.imagePreview = null;
    },

    // Remove edit extra image
    removeEditExtraImage() {
      this.editingExtra.image = null;
      this.editingExtra.imagePreview = null;
    },

    // Set extra type (general or rateset)
    setExtraType(type) {
      this.extraType = type;
      this.newExtra.is_addon = type === 'rateset'; // Update the is_addon field
      if (type === 'general') {
        // Keep published status as is (user controls via toggle)
        this.newExtra.associated_rate_sets = [];
        this.newExtra.pricing_basis = 'per_item'; // Reset to per_item for general extras
      } else if (type === 'rateset') {
        this.newExtra.published = false;
        // Keep associated_rate_sets as is, user will select them
        // Keep pricing_basis as is, user can select per_day/per_night
      }
    },

    // Get the current edit extra type based on is_addon field
    getEditExtraType() {
      return this.editingExtra.is_addon ? 'rateset' : 'general';
    },

    // Set edit extra type
    setEditExtraType(type, event) {
      // If clicking on the same type, do nothing
      if (this.editExtraType === type) {
        return;
      }

      // Check if user is trying to change from add-on to general extra
      if (this.editExtraType === 'rateset' && type === 'general') {
        const hasSelectedListings =
          this.editingExtra.associated_rate_sets &&
          this.editingExtra.associated_rate_sets.length > 0;

        if (hasSelectedListings) {
          // Show confirmation popup
          if (
            !confirm(
              'Warning: This add-on will no longer appear on the selected listings. Are you sure you want to change it to a general extra?',
            )
          ) {
            // User cancelled, prevent the default radio button behavior
            if (event) {
              event.preventDefault();
            }
            return;
          }
        }
      }

      this.editExtraType = type; // Set the property for reactive updates
      this.editingExtra.is_addon = type === 'rateset'; // Update the is_addon field

      if (type === 'general') {
        // Clear rate set associations and reset to general
        this.editingExtra.associated_rate_sets = [];
        this.editingExtra.pricing_basis = 'per_item'; // Reset to per_item for general extras
        // Keep published status as is (user controls via toggle)
      } else if (type === 'rateset') {
        // Set to add-on mode
        this.editingExtra.published = false; // Add-ons are always unpublished
        // Keep associated_rate_sets as is, user will select them
        // Keep pricing_basis as is, user can select per_day/per_night
      }
    },

    // Initialize edit modal state based on loaded data
    initializeEditModalState() {
      // This method ensures the edit modal displays the correct state
      // based on the loaded extra data
      // Set the editExtraType property for reactive updates
      this.editExtraType = this.editingExtra.is_addon ? 'rateset' : 'general';

      if (this.editingExtra.is_addon) {
        // This is a listing add-on, ensure it's unpublished
        this.editingExtra.published = false;
      }
      // If no rate sets, it's a general extra (published status is preserved from server)
    },

    // Handle image upload
    async handleExtraImageUpload(event, mode = 'new') {
      // First, check if Pica is available
      if (typeof Pica !== 'function') {
        console.error(
          'Pica library not found. Make sure it is properly imported.',
        );
        // Fallback to basic handling
        const file = event.target.files[0];
        if (!file) return;

        // Check file size
        const maxSizeInBytes = 500 * 1024; // 500 KB
        if (file.size > maxSizeInBytes) {
          alert('Image file size must be less than 500KB');
          event.target.value = ''; // Clear the input
          return;
        }

        // Store the file for form submission
        if (mode === 'edit') {
          this.editingExtra.image = file;
        } else {
          this.newExtra.image = file;
        }

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          if (mode === 'edit') {
            this.editingExtra.imagePreview = e.target.result;
          } else {
            this.newExtra.imagePreview = e.target.result;
          }
        };

        reader.readAsDataURL(file);
        return;
      }

      const pica = new Pica({
        features: ['js', 'wasm'], // Use JS and WASM but not web workers due to CSP restrictions
      });
      const file = event.target.files[0];
      if (!file) return;

      const maxSizeInBytes = 500 * 1024; // 500 KB
      const maxWidth = 1920;
      const maxHeight = 1080;

      if (mode === 'new') {
        this.createExtraImageProcessing = true;
      } else {
        this.imageProcessing = true;
      }

      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const img = new Image();
          img.src = e.target.result;

          img.onload = async () => {
            // Create initial canvas for aspect ratio correction
            let canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d', { willReadFrequently: true });

            console.log(
              'Extra image processing - Dimensions:',
              img.width,
              'x',
              img.height,
            );

            const aspectRatio = img.width / img.height;
            const isTallerThan16_9 = aspectRatio < 16 / 9;

            if (isTallerThan16_9) {
              // Images taller than 16:9: Use smart processing for transparent/uniform border cases
              console.log(
                'Processing image taller than 16:9 with smart border detection',
              );
              processVerticalImageSmart(img, ctx, canvas);
            } else {
              // Square and Horizontal images: NO PROCESSING - use original
              console.log('Using original image - no processing needed');

              canvas.width = img.width;
              canvas.height = img.height;
              ctx.drawImage(img, 0, 0, img.width, img.height);
            }

            // Resize if needed using Pica
            if (canvas.width > maxWidth || canvas.height > maxHeight) {
              const resizedCanvas = document.createElement('canvas');
              const width = canvas.width;
              const height = canvas.height;
              const scaleFactor = Math.min(
                maxWidth / width,
                maxHeight / height,
              );
              resizedCanvas.width = width * scaleFactor;
              resizedCanvas.height = height * scaleFactor;

              await pica.resize(canvas, resizedCanvas);
              canvas = resizedCanvas;
            }

            // Detect if image has transparency
            const hasTransparencyFlag = this.detectTransparency(canvas);

            // Choose format based on transparency
            const format = hasTransparencyFlag ? 'image/png' : 'image/jpeg';
            const quality = hasTransparencyFlag ? undefined : 0.9; // JPEG quality

            let blob = await new Promise((resolve) => {
              canvas.toBlob(resolve, format, quality);
            });

            // Resize if the file size is greater than maxSizeInBytes or dimensions exceed maxWidth/maxHeight
            if (
              blob.size > maxSizeInBytes ||
              canvas.width > maxWidth ||
              canvas.height > maxHeight
            ) {
              const resizedCanvas = document.createElement('canvas');
              const width = canvas.width;
              const height = canvas.height;
              const scaleFactor = Math.min(
                maxWidth / width,
                maxHeight / height,
                Math.sqrt(maxSizeInBytes / blob.size),
              );
              resizedCanvas.width = width * scaleFactor;
              resizedCanvas.height = height * scaleFactor;

              await pica.resize(canvas, resizedCanvas);

              // Use the same format detection for resized image
              const resizedHasTransparencyFlag =
                this.detectTransparency(resizedCanvas);
              const resizedFormat = resizedHasTransparencyFlag
                ? 'image/png'
                : 'image/jpeg';
              const resizedQuality = resizedHasTransparencyFlag
                ? undefined
                : 0.9;

              blob = await new Promise((resolve) => {
                resizedCanvas.toBlob(resolve, resizedFormat, resizedQuality);
              });

              // Update canvas for preview
              canvas = resizedCanvas;
            }

            const dataUrl = canvas.toDataURL(format, quality);

            // Store both the processed blob and the data URL preview
            if (mode === 'edit') {
              // Create filename with correct extension based on format
              const extension = hasTransparencyFlag ? '.png' : '.jpg';
              const fileName = file.name.replace(/\.[^/.]+$/, '') + extension;
              this.editingExtra.image = new File([blob], fileName, {
                type: format,
              });
              this.editingExtra.imagePreview = dataUrl;
            } else {
              // Create filename with correct extension based on format
              const extension = hasTransparencyFlag ? '.png' : '.jpg';
              const fileName = file.name.replace(/\.[^/.]+$/, '') + extension;
              this.newExtra.image = new File([blob], fileName, {
                type: format,
              });
              this.newExtra.imagePreview = dataUrl;
            }
            if (mode === 'new') {
              this.createExtraImageProcessing = false;
            } else {
              this.imageProcessing = false;
            }
          };
        };

        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error handling image upload:', error);
        if (mode === 'new') {
          this.createExtraImageProcessing = false;
        } else {
          this.imageProcessing = false;
        }
        alert(
          'Error processing image. Please try again with a different image.',
        );
      }
    },

    // Create a new extra
    async createExtra() {
      this.isLoading = true;

      try {
        const formData = new FormData();

        formData.append('calendar', this.calendarId);
        formData.append('name', this.newExtra.name);
        formData.append('description', this.newExtra.description || '');
        formData.append('unit_price', this.newExtra.unit_price);
        formData.append('pricing_basis', this.newExtra.pricing_basis);
        formData.append(
          'duration_adjustment',
          this.newExtra.duration_adjustment || 0,
        );

        // Add tax_fees as a JSON string
        if (this.newExtra.tax_fees && this.newExtra.tax_fees.length > 0) {
          // Extract just the IDs from the tax_fees array
          const taxFeeIds = this.newExtra.tax_fees.map((tf) => tf.id);
          formData.append('tax_fees', JSON.stringify(taxFeeIds));
        }

        formData.append(
          'published',
          this.newExtra.published ? 'true' : 'false',
        );
        formData.append('is_addon', this.newExtra.is_addon ? 'true' : 'false');

        // Add associated rate sets
        if (
          this.newExtra.associated_rate_sets &&
          this.newExtra.associated_rate_sets.length > 0
        ) {
          this.newExtra.associated_rate_sets.forEach((rateSetId) => {
            formData.append('associated_rate_sets', rateSetId);
          });
        }

        // Add dropdown field configuration
        if (this.newExtra.dropdown_field_title) {
          formData.append(
            'dropdown_field_title',
            this.newExtra.dropdown_field_title,
          );
        }
        if (this.newExtra.dropdown_options_text) {
          // Convert comma-separated string to JSON array
          const options = this.newExtra.dropdown_options_text
            .split(',')
            .map((option) => option.trim())
            .filter((option) => option.length > 0);
          if (options.length > 0) {
            formData.append('dropdown_options', JSON.stringify(options));
          }
        }
        formData.append(
          'unit_label_singular',
          this.newExtra.unit_label_singular || '',
        );
        formData.append(
          'unit_label_plural',
          this.newExtra.unit_label_plural || '',
        );

        console.log('Sending data:', {
          calendar: this.calendarId,
          name: this.newExtra.name,
          description: this.newExtra.description,
          unit_price: this.newExtra.unit_price,
          tax_fees: this.newExtra.tax_fees,
          published: this.newExtra.published,
          associated_rate_sets: this.newExtra.associated_rate_sets,
          dropdown_field_title: this.newExtra.dropdown_field_title,
          dropdown_options_text: this.newExtra.dropdown_options_text,
        });

        if (this.newExtra.image) {
          formData.append('image', this.newExtra.image);
        }

        // Get CSRF token
        const csrfToken = document.querySelector(
          '[name=csrfmiddlewaretoken]',
        ).value;

        const response = await fetch('/products/create-extra/', {
          method: 'POST',
          headers: {
            'X-CSRFToken': csrfToken,
          },
          body: formData,
        });

        const data = await response.json();
        console.log('Create extra response:', data);

        if (!response.ok) {
          throw new Error(data.error || 'Failed to create extra');
        }

        // Success - close modal and add new extra to the list
        this.showCreateExtraModal = false;
        this.resetNewExtra();

        // Add the new extra to the front-end list if it's returned
        if (data.extra) {
          this.extras.push(data.extra);
          console.log('Added new extra to list:', data.extra);
          console.log('Current extras array:', this.extras);
        } else {
          console.error('No extra data in response:', data);
        }
      } catch (error) {
        console.error('Error creating extra:', error);
        alert(error.message || 'An error occurred while creating the extra');
      } finally {
        this.isLoading = false;
      }
    },

    // Edit an existing extra
    async editExtra(extraId) {
      try {
        // Find the basic extra info in the list
        const extra = this.extras.find((e) => e.id === extraId);
        if (!extra) {
          console.error('Extra not found:', extraId);
          return;
        }

        // Fetch the full extra details including tax_fees
        const response = await fetch(
          `/calendar/full-extra-details/${this.calendarId}/${extraId}/`,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );

        if (!response.ok) throw new Error('Failed to fetch extra details');

        const fullExtra = await response.json();
        console.log('Full extra details:', fullExtra);

        // Set the editing extra with complete data
        this.editingExtra = {
          id: fullExtra.id,
          name: fullExtra.name,
          description: fullExtra.description || '',
          unit_price: fullExtra.unit_price,
          imagePreview: fullExtra.image_url,
          published: fullExtra.published,
          is_addon: fullExtra.is_addon || false,
          tax_fees: fullExtra.tax_fees || [],
          associated_rate_sets: fullExtra.associated_rate_sets || [],
          dropdown_field_title: fullExtra.dropdown_field_title || '',
          dropdown_options_text: fullExtra.dropdown_options
            ? fullExtra.dropdown_options.join(', ')
            : '',
          unit_label_singular: fullExtra.unit_label_singular || '',
          unit_label_plural: fullExtra.unit_label_plural || '',
          pricing_basis: fullExtra.pricing_basis || 'per_item',
          duration_adjustment: fullExtra.duration_adjustment || 0,
        };

        console.log('Initialized editingExtra:', this.editingExtra);

        // Initialize the edit modal state based on the loaded data
        this.initializeEditModalState();

        // Show the modal
        this.showEditExtraModal = true;
      } catch (error) {
        console.error('Error opening edit modal:', error);
        const toastStore = Alpine.store('toastStore');
        if (toastStore && toastStore.addToast) {
          toastStore.addToast('Failed to load extra details', 'danger');
        }
      }
    },

    // Update an extra
    async updateExtra() {
      if (!this.calendarId || !this.editingExtra.id) {
        console.error('Calendar ID or Extra ID not available');
        alert(
          'Calendar ID or Extra ID not available. Please refresh the page and try again.',
        );
        return;
      }

      this.isLoading = true;

      try {
        const formData = new FormData();

        formData.append('extra_id', this.editingExtra.id);
        formData.append('calendar', this.calendarId);
        formData.append('name', this.editingExtra.name);
        formData.append('description', this.editingExtra.description || '');
        formData.append('unit_price', this.editingExtra.unit_price);
        formData.append('pricing_basis', this.editingExtra.pricing_basis);
        formData.append(
          'duration_adjustment',
          this.editingExtra.duration_adjustment || 0,
        );
        formData.append(
          'published',
          this.editingExtra.published ? 'true' : 'false',
        );
        formData.append(
          'is_addon',
          this.editingExtra.is_addon ? 'true' : 'false',
        );

        // Always send tax_fees, even if empty array
        const taxFeeIds = (this.editingExtra.tax_fees || []).map((tf) => tf.id);
        formData.append('tax_fees', JSON.stringify(taxFeeIds));

        // Add associated rate sets (always send, even if empty)
        if (this.editingExtra.associated_rate_sets) {
          this.editingExtra.associated_rate_sets.forEach((rateSetId) => {
            formData.append('associated_rate_sets', rateSetId);
          });
        }

        // Add dropdown field configuration
        if (this.editingExtra.dropdown_field_title) {
          formData.append(
            'dropdown_field_title',
            this.editingExtra.dropdown_field_title,
          );
        }
        if (this.editingExtra.dropdown_options_text) {
          // Convert comma-separated string to JSON array
          const options = this.editingExtra.dropdown_options_text
            .split(',')
            .map((option) => option.trim())
            .filter((option) => option.length > 0);
          if (options.length > 0) {
            formData.append('dropdown_options', JSON.stringify(options));
          }
        }
        formData.append(
          'unit_label_singular',
          this.editingExtra.unit_label_singular || '',
        );
        formData.append(
          'unit_label_plural',
          this.editingExtra.unit_label_plural || '',
        );

        if (this.editingExtra.newImage) {
          formData.append('image', this.editingExtra.newImage);
        }

        const response = await fetch('/products/update-extra/', {
          method: 'POST',
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
          body: formData,
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to update extra');
        }

        // Success - close modal and update extra in the list
        this.closeEditExtraModal();

        // Update the extra in the front-end list if it's returned
        if (data.extra) {
          const index = this.extras.findIndex((e) => e.id === data.extra.id);
          if (index !== -1) {
            this.extras[index] = data.extra;
            console.log('Updated extra in list:', data.extra);
          }
        }
      } catch (error) {
        console.error('Error updating extra:', error);
        alert(error.message || 'An error occurred while updating the extra');
      } finally {
        this.isLoading = false;
      }
    },

    // Close edit modal
    closeEditExtraModal() {
      this.showEditExtraModal = false;
      // Let Alpine finish hide transitions before resetting bound modal data.
      window.setTimeout(() => {
        // If modal got re-opened quickly, avoid wiping active edit state.
        if (this.showEditExtraModal) return;

        this.editingExtra = {
          id: null,
          name: '',
          description: '',
          unit_price: 0,
          image: null,
          imagePreview: null,
          published: false,
          is_addon: false,
          tax_fees: [],
          associated_rate_sets: [],
          dropdown_field_title: '',
          dropdown_options_text: '',
          unit_label_singular: '',
          unit_label_plural: '',
          pricing_basis: 'per_item',
        };
      }, 220);
    },

    // Handle image upload for editing
    async handleEditExtraImageUpload(event) {
      // First, check if Pica is available
      console.log(
        'handleEditExtraImageUpload - Pica type check:',
        typeof Pica,
        'Pica value:',
        Pica,
      );
      if (typeof Pica !== 'function') {
        console.error(
          'Pica library not found. Make sure it is properly imported.',
        );
        // Fallback to basic handling
        const file = event.target.files[0];
        if (!file) return;

        // Check file size
        const maxSizeInBytes = 500 * 1024; // 500 KB
        if (file.size > maxSizeInBytes) {
          alert('Image file size must be less than 500KB');
          event.target.value = ''; // Clear the input
          return;
        }

        // Store the file for form submission
        this.editingExtra.newImage = file;

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.editingExtra.imagePreview = e.target.result;
        };

        reader.readAsDataURL(file);
        return;
      }

      const pica = new Pica({
        features: ['js', 'wasm'], // Use JS and WASM but not web workers due to CSP restrictions
      });
      const file = event.target.files[0];
      if (!file) return;

      const maxSizeInBytes = 500 * 1024; // 500 KB
      const maxWidth = 1920;
      const maxHeight = 1080;

      this.imageProcessing = true;

      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const img = new Image();
          img.src = e.target.result;

          img.onload = async () => {
            // Create initial canvas for aspect ratio correction
            let canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d', { willReadFrequently: true });

            console.log(
              'Extra image processing - Dimensions:',
              img.width,
              'x',
              img.height,
            );

            const aspectRatio = img.width / img.height;
            const isTallerThan16_9 = aspectRatio < 16 / 9;

            if (isTallerThan16_9) {
              // Images taller than 16:9: Use smart processing for transparent/uniform border cases
              console.log(
                'Processing image taller than 16:9 with smart border detection',
              );
              processVerticalImageSmart(img, ctx, canvas);
            } else {
              // Square and Horizontal images: NO PROCESSING - use original
              console.log('Using original image - no processing needed');

              canvas.width = img.width;
              canvas.height = img.height;
              ctx.drawImage(img, 0, 0, img.width, img.height);
            }

            // Resize if needed using Pica
            if (canvas.width > maxWidth || canvas.height > maxHeight) {
              const resizedCanvas = document.createElement('canvas');
              const width = canvas.width;
              const height = canvas.height;
              const scaleFactor = Math.min(
                maxWidth / width,
                maxHeight / height,
              );
              resizedCanvas.width = width * scaleFactor;
              resizedCanvas.height = height * scaleFactor;

              await pica.resize(canvas, resizedCanvas);
              canvas = resizedCanvas;
            }

            // Detect if image has transparency
            const hasTransparencyFlag = this.detectTransparency(canvas);

            // Choose format based on transparency
            const format = hasTransparencyFlag ? 'image/png' : 'image/jpeg';
            const quality = hasTransparencyFlag ? undefined : 0.9; // JPEG quality

            let blob = await new Promise((resolve) => {
              canvas.toBlob(resolve, format, quality);
            });

            // Resize if the file size is greater than maxSizeInBytes or dimensions exceed maxWidth/maxHeight
            if (
              blob.size > maxSizeInBytes ||
              canvas.width > maxWidth ||
              canvas.height > maxHeight
            ) {
              const resizedCanvas = document.createElement('canvas');
              const width = canvas.width;
              const height = canvas.height;
              const scaleFactor = Math.min(
                maxWidth / width,
                maxHeight / height,
                Math.sqrt(maxSizeInBytes / blob.size),
              );
              resizedCanvas.width = width * scaleFactor;
              resizedCanvas.height = height * scaleFactor;

              await pica.resize(canvas, resizedCanvas);

              // Use the same format detection for resized image
              const resizedHasTransparencyFlag =
                this.detectTransparency(resizedCanvas);
              const resizedFormat = resizedHasTransparencyFlag
                ? 'image/png'
                : 'image/jpeg';
              const resizedQuality = resizedHasTransparencyFlag
                ? undefined
                : 0.9;

              blob = await new Promise((resolve) => {
                resizedCanvas.toBlob(resolve, resizedFormat, resizedQuality);
              });

              // Update canvas for preview
              canvas = resizedCanvas;
            }

            const dataUrl = canvas.toDataURL(format, quality);

            // Store both the processed blob and the data URL preview
            // Create filename with correct extension based on format
            const extension = hasTransparencyFlag ? '.png' : '.jpg';
            const fileName = file.name.replace(/\.[^/.]+$/, '') + extension;
            this.editingExtra.newImage = new File([blob], fileName, {
              type: format,
            });
            this.editingExtra.imagePreview = dataUrl;
            this.imageProcessing = false;
          };
        };

        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error handling image upload:', error);
        this.imageProcessing = false;
      }
    },

    // Delete an extra
    async deleteExtra(extraId) {
      if (!this.calendarId || !extraId) {
        console.error('Calendar ID or Extra ID not available');
        alert(
          'Calendar ID or Extra ID not available. Please refresh the page and try again.',
        );
        return;
      }

      this.isLoading = true;

      try {
        // Get CSRF token
        const csrfToken = document.querySelector(
          '[name=csrfmiddlewaretoken]',
        ).value;

        const response = await fetch('/products/delete-extra/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify({
            extra_id: extraId,
            calendar: this.calendarId,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to delete extra');
        }

        // Success - remove extra from the list
        this.extras = this.extras.filter((e) => e.id !== extraId);
        console.log('Removed extra from list:', extraId);
      } catch (error) {
        console.error('Error deleting extra:', error);
        alert(error.message || 'An error occurred while deleting the extra');
      } finally {
        this.isLoading = false;
      }
    },
    formatCurrency(amount, currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(amount));
    },
    // Add tax/fee to the extra (works for both new and editing)
    addTaxFee(taxFeeId) {
      if (!taxFeeId) return;

      const numericId = Number(taxFeeId);
      const tax = this.availableTaxes.find((t) => t.id === numericId);
      const fee = this.availableFees.find((f) => f.id === numericId);
      const taxFee = tax || fee;

      if (taxFee) {
        // Handle both newExtra and editingExtra
        const targetExtra = this.showEditExtraModal
          ? this.editingExtra
          : this.newExtra;

        if (!targetExtra.tax_fees) {
          targetExtra.tax_fees = [];
        }

        if (!targetExtra.tax_fees.some((tf) => tf.id === taxFee.id)) {
          targetExtra.tax_fees.push(taxFee);
        }
      }
    },

    // Remove tax/fee from the extra (works for both new and editing)
    removeTaxFee(taxFeeId) {
      // Handle both newExtra and editingExtra
      const targetExtra = this.showEditExtraModal
        ? this.editingExtra
        : this.newExtra;

      if (targetExtra.tax_fees) {
        targetExtra.tax_fees = targetExtra.tax_fees.filter(
          (tf) => tf.id !== taxFeeId,
        );
      }
    },

    // Helper methods for filtering available taxes/fees
    getAvailableTaxes() {
      return (this.availableTaxes || []).filter(
        (t) => !(this.newExtra.tax_fees || []).some((tf) => tf.id === t.id),
      );
    },

    getAvailableFees() {
      return (this.availableFees || []).filter(
        (f) => !(this.newExtra.tax_fees || []).some((tf) => tf.id === f.id),
      );
    },

    destroyQuillHelpText() {
      // Simply clear the store reference
      // The DOM element will be removed by Alpine's x-if when modal closes
      if (this.quillHelpText) {
        try {
          if (this.quillHelpText.off) {
            this.quillHelpText.off('text-change');
          }
        } catch (e) {
          // Ignore errors
        }
        this.quillHelpText = null;
      }
    },

    initQuillHelpText(element) {
      // This is called from x-init when the template x-if renders a fresh element
      // Since x-if completely removes and recreates the element, we get a fresh DOM each time

      if (!element) {
        console.error('Help text Quill element is null or undefined');
        return;
      }

      // Clear any previous store reference (from previous modal open)
      if (this.quillHelpText) {
        this.quillHelpText = null;
      }

      const toolbarOptions = [
        [{ header: [4, 5, false] }], // Only headers 4 and 5, and normal text
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ];

      try {
        this.quillHelpText = new Quill(element, {
          theme: 'snow',
          modules: {
            toolbar: toolbarOptions,
            clipboard: {
              matchVisual: false,
            },
          },
          formats: ['header', 'bold', 'italic', 'underline', 'list'],
        });

        // Set initial content from currentListingField.help_text
        setTimeout(() => {
          if (this.quillHelpText) {
            this.quillHelpText.root.innerHTML =
              this.currentListingField.help_text || '';

            // Update store when Quill content changes
            this.quillHelpText.on('text-change', () => {
              this.currentListingField.help_text =
                this.quillHelpText.root.innerHTML;
            });
          }
        }, 0);
      } catch (error) {
        console.error('Error initializing Help Text Quill:', error);
      }
    },

    // Store Customization Methods
    initializeStoreCustomization() {
      // Load themes from script tag if available
      const themesScript = document.getElementById('store-themes-data');
      if (themesScript) {
        this.themes = JSON.parse(themesScript.textContent);
      }

      // Get current values from server-rendered template
      const container = document.getElementById('calendarSettingsContainer');
      if (container) {
        this.customStoreColor = container.dataset.storeColor || '#ffffff';
        this.customButtonColor =
          container.dataset.storeButtonColor || '#212529';
        this.storeHeaderColor = container.dataset.storeHeaderColor || '#ffffff';
        this.sharpMode = container.dataset.sharpMode === 'true';
        this.roundedMode = container.dataset.roundedMode === 'true';
        this.imageDisplayMode = container.dataset.imageDisplayMode || 'LS';

        // Determine if header is currently set to match store color
        this.headerShouldMatchStore =
          this.storeHeaderColor !== '#ffffff' &&
          this.storeHeaderColor === this.customStoreColor;
      }

      // Determine current theme based on colors
      this.updateSelectedTheme();
    },

    updateSelectedTheme() {
      let matchedTheme = 'classic';
      let foundMatch = false;

      for (const [key, theme] of Object.entries(this.themes)) {
        if (
          !theme.is_custom &&
          theme.store_color === this.customStoreColor &&
          theme.button_color === this.customButtonColor
        ) {
          matchedTheme = key;
          foundMatch = true;
          break;
        }
      }

      if (!foundMatch && this.themes.custom) {
        matchedTheme = 'custom';
      }

      this.selectedTheme = matchedTheme;
    },

    selectTheme(themeKey) {
      this.selectedTheme = themeKey;

      if (themeKey === 'custom') {
        this.updateCustomColors();
      } else if (this.themes[themeKey]) {
        this.customStoreColor = this.themes[themeKey].store_color;
        this.customButtonColor = this.themes[themeKey].button_color;
        // No need to manually update form fields - Alpine.js binding will handle it
      }

      // Update header color if it should match store color
      this.updateHeaderColorIfMatching();
    },

    updateCustomColors() {
      // No need to manually update form fields - Alpine.js binding will handle it
      this.updateHeaderColorIfMatching();
    },

    selectHeaderStyle(shouldMatch) {
      this.headerShouldMatchStore = shouldMatch;
      if (shouldMatch) {
        this.storeHeaderColor = this.customStoreColor;
      } else {
        this.storeHeaderColor = '#ffffff';
      }
      // No need to manually update form field - Alpine.js binding will handle it
    },

    updateHeaderColorIfMatching() {
      // Only update if user has chosen to match header to store color
      if (this.headerShouldMatchStore) {
        this.storeHeaderColor = this.customStoreColor;
        // No need to manually update form field - Alpine.js binding will handle it
      }
    },

    selectStyle(style) {
      this.sharpMode = style === 'sharp';
      this.roundedMode = style === 'rounded';
      // No need to manually update form fields - Alpine.js binding will handle it
    },

    get selectedStyle() {
      if (this.sharpMode) return 'sharp';
      if (this.roundedMode) return 'rounded';
      return 'standard';
    },

    get isMatchingStoreColor() {
      return this.headerShouldMatchStore;
    },

    get isWhiteBackground() {
      return this.storeHeaderColor === '#ffffff';
    },

    selectImageDisplayMode(mode) {
      this.imageDisplayMode = mode;
      // No need to manually update form field - Alpine.js binding will handle it
    },

    async fetchCustomFields() {
      try {
        const response = await fetch('/customers/api/custom-fields/', {
          method: 'GET',
          headers: {
            'X-CSRFToken': getCSRFToken(),
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          this.customFields = await response.json();
        } else {
          console.error('Failed to fetch custom fields');
        }
      } catch (error) {
        console.error('Error fetching custom fields:', error);
      }
    },

    openAddFieldModal() {
      this.showAddFieldModal = true;
      this.showEditFieldModal = false;
      this.resetCurrentField();
    },

    editCustomField(field) {
      this.showEditFieldModal = true;
      this.showAddFieldModal = false;
      this.currentField = { ...field };

      // Convert dropdown options array to text for editing
      if (field.field_type === 'dropdown' && field.options) {
        this.dropdownOptionsText = field.options.join('\n');
      } else {
        this.dropdownOptionsText = '';
      }
    },

    closeFieldModal() {
      this.showAddFieldModal = false;
      this.showEditFieldModal = false;
      this.resetCurrentField();
    },

    resetCurrentField() {
      this.currentField = {
        field_type: 'text',
        field_label: '',
        field_placeholder: '', // Will be automatically set to match field_label when saving
        is_active: true,
        order: 0,
        options: [],
        validation_rules: {},
      };
      this.dropdownOptionsText = '';
    },

    async saveCustomField() {
      this.isSavingField = true;

      try {
        const fieldData = { ...this.currentField };

        // Automatically set placeholder to match field label
        fieldData.field_placeholder = fieldData.field_label;

        // Handle dropdown options
        if (fieldData.field_type === 'dropdown') {
          fieldData.options = this.dropdownOptionsText
            .split('\n')
            .map((opt) => opt.trim())
            .filter((opt) => opt.length > 0);
        }

        const url = this.showEditFieldModal
          ? `/customers/api/custom-fields/${this.currentField.id}/`
          : '/customers/api/custom-fields/';

        const method = this.showEditFieldModal ? 'PUT' : 'POST';

        const response = await fetch(url, {
          method: method,
          headers: {
            'X-CSRFToken': getCSRFToken(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(fieldData),
        });

        if (response.ok) {
          await this.fetchCustomFields(); // Refresh the list
          this.closeFieldModal();

          // Show success message
          if (window.Alpine && Alpine.store('toastStore')) {
            Alpine.store('toastStore').addToast(
              this.showEditFieldModal
                ? 'Custom field updated successfully!'
                : 'Custom field created successfully!',
              'success',
            );
          }
        } else {
          const errorData = await response.json();
          console.error('Failed to save custom field:', errorData);

          if (window.Alpine && Alpine.store('toastStore')) {
            Alpine.store('toastStore').addToast(
              'Failed to save custom field. Please try again.',
              'danger',
            );
          }
        }
      } catch (error) {
        console.error('Error saving custom field:', error);

        if (window.Alpine && Alpine.store('toastStore')) {
          Alpine.store('toastStore').addToast(
            'An error occurred while saving the custom field.',
            'danger',
          );
        }
      } finally {
        this.isSavingField = false;
      }
    },

    async toggleCustomFieldStatus(fieldId, currentStatus) {
      const action = currentStatus ? 'deactivate' : 'activate';
      const actionText = currentStatus ? 'deactivate' : 'activate';

      if (
        !confirm(`Are you sure you want to ${actionText} this custom field?`)
      ) {
        return;
      }

      try {
        const response = await fetch(
          `/customers/api/custom-fields/${fieldId}/toggle-status/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              is_active: !currentStatus,
            }),
          },
        );

        if (response.ok) {
          await this.fetchCustomFields(); // Refresh the list

          if (window.Alpine && Alpine.store('toastStore')) {
            Alpine.store('toastStore').addToast(
              `Custom field ${actionText}d successfully!`,
              'success',
            );
          }
        } else {
          console.error(`Failed to ${actionText} custom field`);

          if (window.Alpine && Alpine.store('toastStore')) {
            Alpine.store('toastStore').addToast(
              `Failed to ${actionText} custom field. Please try again.`,
              'danger',
            );
          }
        }
      } catch (error) {
        console.error(`Error ${actionText}ing custom field:`, error);

        if (window.Alpine && Alpine.store('toastStore')) {
          Alpine.store('toastStore').addToast(
            `An error occurred while ${actionText}ing the custom field.`,
            'danger',
          );
        }
      }
    },

    // Listing Custom Fields Methods
    async fetchListingFields() {
      try {
        const response = await fetch('/rates/api/custom-fields/definitions/', {
          method: 'GET',
          headers: {
            'X-CSRFToken': getCSRFToken(),
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          this.listingFields = await response.json();
          this.calculateRatesetFieldCounts();
        } else {
          console.error('Failed to fetch custom fields');
        }
      } catch (error) {
        console.error('Error fetching custom fields:', error);
      }
    },

    calculateRatesetFieldCounts() {
      // Count how many active custom fields are assigned to each rateset
      this.ratesetFieldCounts = {};

      this.listingFields.forEach((field) => {
        if (!field.is_active) {
          return;
        }
        if (
          field.rateset_assignments &&
          Array.isArray(field.rateset_assignments)
        ) {
          field.rateset_assignments.forEach((assignment) => {
            if (assignment.is_active === false) {
              return;
            }
            const ratesetId = assignment.rate_set || assignment.rate_set_id;
            if (ratesetId) {
              this.ratesetFieldCounts[ratesetId] =
                (this.ratesetFieldCounts[ratesetId] || 0) + 1;
            }
          });
        }
      });
    },

    isRatesetDisabledForField(ratesetId) {
      const currentFieldRatesets =
        this.currentListingField.selected_ratesets || [];
      const isCurrentlySelected = currentFieldRatesets.includes(
        String(ratesetId),
      );

      // If this rateset is already selected in the current field being edited, don't disable it
      if (isCurrentlySelected) {
        return false;
      }

      const count = this.ratesetFieldCounts[ratesetId] || 0;
      return count >= this.maxListingCustomFields;
    },

    openAddListingFieldModal() {
      // Clean up any previous Quill instance
      this.destroyQuillHelpText();
      this.showAddListingFieldModal = true;
      this.showEditListingFieldModal = false;
      this.resetCurrentListingField();
      // x-init in template handles Quill initialization
    },

    editListingField(field) {
      // Clean up any previous Quill instance
      this.destroyQuillHelpText();
      this.showEditListingFieldModal = true;
      this.showAddListingFieldModal = false;
      this.currentListingField = { ...field };

      // Convert dropdown options array to text for editing
      if (field.field_type === 'dropdown' && field.options) {
        this.listingDropdownOptionsText = field.options.join('\n');
      } else {
        this.listingDropdownOptionsText = '';
      }

      // Load selected ratesets from assignments
      if (
        field.rateset_assignments &&
        Array.isArray(field.rateset_assignments)
      ) {
        this.currentListingField.selected_ratesets =
          field.rateset_assignments.map((assignment) =>
            String(assignment.rate_set || assignment.rate_set_id),
          );
      } else {
        this.currentListingField.selected_ratesets = [];
      }

      // x-init in template handles Quill initialization
    },

    closeListingFieldModal() {
      // Clean up Quill instance before closing
      this.destroyQuillHelpText();
      this.showAddListingFieldModal = false;
      this.showEditListingFieldModal = false;
      this.resetCurrentListingField();
    },

    resetCurrentListingField() {
      // Clean up Quill instance
      if (this.quillHelpText) {
        this.quillHelpText = null;
      }

      this.currentListingField = {
        field_type: 'text',
        field_label: '',
        field_placeholder: '',
        is_required: true,
        is_active: true,
        applies_to: 'booking_item',
        order: 0,
        options: [],
        help_text: '',
        validation_rules: {},
        selected_ratesets: [],
      };
      this.listingDropdownOptionsText = '';
    },

    async saveListingField() {
      this.isSavingListingField = true;

      try {
        const fieldData = { ...this.currentListingField };

        // Handle dropdown options
        if (fieldData.field_type === 'dropdown') {
          fieldData.options = this.listingDropdownOptionsText
            .split('\n')
            .map((opt) => opt.trim())
            .filter((opt) => opt.length > 0);
        }

        const url = this.showEditListingFieldModal
          ? `/rates/api/custom-fields/definitions/${this.currentListingField.id}/`
          : '/rates/api/custom-fields/definitions/';

        const method = this.showEditListingFieldModal ? 'PUT' : 'POST';

        const response = await fetch(url, {
          method: method,
          headers: {
            'X-CSRFToken': getCSRFToken(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(fieldData),
        });

        if (response.ok) {
          await this.fetchListingFields(); // Refresh the list
          this.closeListingFieldModal();

          // Show success message
          if (window.Alpine && Alpine.store('toastStore')) {
            Alpine.store('toastStore').addToast(
              this.showEditListingFieldModal
                ? 'Custom field updated successfully!'
                : 'Custom field created successfully!',
              'success',
            );
          }
        } else {
          const errorData = await response.json();
          console.error('Failed to save custom field:', errorData);

          if (window.Alpine && Alpine.store('toastStore')) {
            Alpine.store('toastStore').addToast(
              'Failed to save custom field. Please try again.',
              'danger',
            );
          }
        }
      } catch (error) {
        console.error('Error saving custom field:', error);

        if (window.Alpine && Alpine.store('toastStore')) {
          Alpine.store('toastStore').addToast(
            'An error occurred while saving the custom field.',
            'danger',
          );
        }
      } finally {
        this.isSavingListingField = false;
      }
    },

    async toggleListingFieldStatus(fieldId, currentStatus) {
      const action = currentStatus ? 'deactivate' : 'activate';
      const actionText = currentStatus ? 'deactivate' : 'activate';

      if (
        !confirm(`Are you sure you want to ${actionText} this custom field?`)
      ) {
        return;
      }

      try {
        const response = await fetch(
          `/rates/api/custom-fields/definitions/${fieldId}/toggle-status/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );

        if (response.ok) {
          await this.fetchListingFields(); // Refresh the list

          if (window.Alpine && Alpine.store('toastStore')) {
            Alpine.store('toastStore').addToast(
              `Custom field ${actionText}d successfully!`,
              'success',
            );
          }
        } else {
          console.error(`Failed to ${actionText} custom field`);

          if (window.Alpine && Alpine.store('toastStore')) {
            Alpine.store('toastStore').addToast(
              `Failed to ${actionText} custom field. Please try again.`,
              'danger',
            );
          }
        }
      } catch (error) {
        console.error(`Error ${actionText}ing custom field:`, error);

        if (window.Alpine && Alpine.store('toastStore')) {
          Alpine.store('toastStore').addToast(
            `An error occurred while ${actionText}ing the custom field.`,
            'danger',
          );
        }
      }
    },

    async deleteListingField(fieldId) {
      if (
        !confirm(
          'Are you sure you want to delete this custom field? This action cannot be undone.',
        )
      ) {
        return;
      }

      try {
        const response = await fetch(
          `/rates/api/custom-fields/definitions/${fieldId}/`,
          {
            method: 'DELETE',
            headers: {
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );

        if (response.ok) {
          await this.fetchListingFields(); // Refresh the list

          if (window.Alpine && Alpine.store('toastStore')) {
            Alpine.store('toastStore').addToast(
              'Custom field deleted successfully!',
              'success',
            );
          }
        } else {
          console.error('Failed to delete custom field');

          if (window.Alpine && Alpine.store('toastStore')) {
            Alpine.store('toastStore').addToast(
              'Failed to delete custom field. Please try again.',
              'danger',
            );
          }
        }
      } catch (error) {
        console.error('Error deleting custom field:', error);

        if (window.Alpine && Alpine.store('toastStore')) {
          Alpine.store('toastStore').addToast(
            'An error occurred while deleting the custom field.',
            'danger',
          );
        }
      }
    },

    // =====================================================================
    // MANUAL DISCOUNT + COUPON CODE METHODS
    // =====================================================================

    _toast(message, type = 'success') {
      const toastStore = Alpine.store('toastStore');
      if (toastStore && toastStore.addToast) {
        toastStore.addToast(message, type);
      }
    },
  });
  // Return the store for potential use elsewhere
  return store;
}
